<script setup>
import {tvCompactTeamName, tvWinner} from '~/utils/tvDisplay';

// A result as a TV scorebug: home over away, one column per game, then the
// match score in a box at the end. The side that won each game is bold; the
// winner's row and score are lit. One grid with fixed-width columns, so every
// game column lines up between the two rows whatever the digits.
//   ┌───────────────┬────┬────┬────┬───┐
//   │ Anna Smith    │ 11 │  9 │ 11 │ 2 │
//   │ Ben Jones     │  7 │ 11 │  4 │ 1 │
//   └───────────────┴────┴────┴────┴───┘
const props = defineProps({
  result: {type: Object, required: true},
  // 'ticker' (one line of the crawl), 'list' (sidebar) or 'hero' (the sting)
  size: {type: String, default: 'list'},
});

const winner = computed(() => tvWinner(props.result));
const games = computed(() => props.result.games ?? []);
const gameWinner = (game) => game.home === game.away ? null : (game.home > game.away ? 'home' : 'away');

// A pair reads "A. Smith & B. Jones" so both partners fit one row; the sting has room for full names
const nameOf = (name) => props.size === 'hero' ? name : tvCompactTeamName(name);
const rows = computed(() => [
  {side: 'home', name: nameOf(props.result.homeTeamName), score: props.result.homeResult},
  {side: 'away', name: nameOf(props.result.awayTeamName), score: props.result.awayResult},
]);

// repeat(0, …) is invalid CSS, so a result without games skips the game columns.
// The score box is square for digits and widens for a word ("Walkover", "Forfeit").
const columns = computed(() => [
  'var(--bug-name)',
  ...games.value.map(() => 'var(--bug-cell)'),
  'minmax(var(--bug-score-cell), max-content)',
].join(' '));
</script>

<template>
  <div class="tv-bug" :class="`tv-bug--${size}`" :style="{gridTemplateColumns: columns}">
    <template v-for="row in rows" :key="row.side">
      <span class="tv-bug-cell tv-bug-name" :class="[`tv-bug--${row.side}`, {'tv-bug-won': winner === row.side}]">{{ row.name }}</span>
      <span v-for="(game, g) in games" :key="g" class="tv-bug-cell tv-bug-game"
            :class="[`tv-bug--${row.side}`, {'tv-bug-game--won': gameWinner(game) === row.side}]">{{ game[row.side] }}</span>
      <span class="tv-bug-cell tv-bug-score" :class="[`tv-bug--${row.side}`, {'tv-bug-score--won': winner === row.side}]">{{ row.score }}</span>
    </template>
  </div>
</template>

<style scoped lang="scss">
.tv-bug {
  --bug-name: 340px;
  --bug-row: 42px;
  --bug-font: 24px;
  --bug-score: 28px;
  --bug-cell: 46px;
  --bug-score-cell: 52px;
  display: inline-grid;
  grid-auto-rows: var(--bug-row);
  border-radius: 6px;
  overflow: hidden;
  background: var(--tv-panel);
  border: 1px solid var(--tv-line);
}

.tv-bug--ticker {
  --bug-name: 240px;
  --bug-row: 38px;
  --bug-font: 21px;
  --bug-score: 24px;
  --bug-cell: 40px;
  --bug-score-cell: 46px;
}

/* In the sidebar and the sting the name takes the slack */
.tv-bug--list,
.tv-bug--hero {
  --bug-name: minmax(0, 1fr);
  display: grid;
}

.tv-bug--hero {
  --bug-row: 72px;
  --bug-font: 44px;
  --bug-score: 52px;
  --bug-cell: 78px;
  --bug-score-cell: 96px;
}

.tv-bug-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  color: var(--tv-muted);
}

/* The away row sits under a rule */
.tv-bug-cell.tv-bug--away {
  border-top: 1px solid var(--tv-line);
}

.tv-bug-name {
  display: block;
  padding: 0 14px;
  letter-spacing: -0.3px;
  font-size: var(--bug-font);
  line-height: var(--bug-row);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-bug-won {
  color: var(--tv-ink);
  font-weight: 600;
  box-shadow: inset 5px 0 0 var(--tv-accent);
}

.tv-bug-game,
.tv-bug-score {
  justify-content: center;
  font-family: var(--tv-display);
  font-variant-numeric: tabular-nums;
  border-left: 1px solid var(--tv-line);
}

.tv-bug-game {
  font-size: var(--bug-font);
  font-weight: 500;
}

.tv-bug-game--won {
  font-weight: 800;
  color: var(--tv-ink);
}

.tv-bug-score {
  padding: 0 10px;
  white-space: nowrap;
  font-size: var(--bug-score);
  font-weight: 800;
  background: var(--tv-chip);
}

.tv-bug-score--won {
  background: var(--tv-accent);
  color: var(--tv-on-accent);
}
</style>
